'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  addCommentAPI,
  deleteCommentAPI,
  fetchComments,
} from './clip-comment-apis';
import type { BasicUserProfileInfo, CommentMessage } from './types';

type UseCommentsHook = {
  comments: CommentMessage[];
  addComment: (
    text: string,
    userId: string,
    userMeta: BasicUserProfileInfo | null,
  ) => Promise<void>;
  retryComment: (
    commentId: string,
    userId: string,
    userMeta: BasicUserProfileInfo | null,
  ) => Promise<void>;
  deleteComment: (
    clipId: string,
    timestamp: number,
    userId: string,
  ) => Promise<void>;
  fetchMoreComments: () => Promise<void>;
  isCommentLoading: boolean;
  isFetchingInitialComments: boolean;
  isFetchingMoreComments: boolean;
  hasMoreComments: boolean;
};

export function useClipComments(clipId: string): UseCommentsHook {
  const [comments, setComments] = useState<CommentMessage[]>([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<{
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null>(null);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [isFetchingInitialComments, setIsFetchingInitialComments] =
    useState(true);
  const [isFetchingMoreComments, setIsFetchingMoreComments] = useState(false);
  const [hasMoreComments, setHasMoreComments] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchInitialComments() {
      setIsFetchingInitialComments(true);
      try {
        const response = await fetchComments({
          clip_id: clipId,
          pageSize: 20,
          lastEvaluatedKey: null,
        });
        if (!cancelled) {
          setComments(response.comments);
          setLastEvaluatedKey(response.lastEvaluatedKey);
          setHasMoreComments(!!response.lastEvaluatedKey);
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        if (!cancelled) setIsFetchingInitialComments(false);
      }
    }

    void fetchInitialComments();
    return () => {
      cancelled = true;
    };
  }, [clipId]);

  // Poll for new comments every 15 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isFetchingMoreComments) return;
      try {
        const response = await fetchComments({
          clip_id: clipId,
          pageSize: 20,
          lastEvaluatedKey: null,
        });
        setComments(prev => {
          if (prev.length < response.totalCount) {
            const merged = [...response.comments, ...prev];
            return Array.from(new Map(merged.map(c => [c.id, c])).values());
          }
          return prev;
        });
      } catch {
        // Polling failure is non-critical
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [clipId, isFetchingMoreComments]);

  const fetchMoreComments = useCallback(async () => {
    if (!lastEvaluatedKey || isFetchingMoreComments) return;
    setIsFetchingMoreComments(true);
    try {
      const response = await fetchComments({
        clip_id: clipId,
        pageSize: 20,
        lastEvaluatedKey,
      });
      setComments(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newComments = response.comments.filter(
          c => !existingIds.has(c.id),
        );
        return [...prev, ...newComments];
      });
      setLastEvaluatedKey(response.lastEvaluatedKey);
      setHasMoreComments(!!response.lastEvaluatedKey);
    } catch (error) {
      console.error('Error fetching more comments:', error);
    } finally {
      setIsFetchingMoreComments(false);
    }
  }, [clipId, lastEvaluatedKey, isFetchingMoreComments]);

  function updateCommentStatus(
    id: string,
    status: CommentMessage['status'],
    newId?: string,
  ) {
    setComments(prev =>
      prev.map(comment =>
        comment.id === id
          ? { ...comment, status, id: newId ?? comment.id }
          : comment,
      ),
    );
  }

  const addComment = useCallback(
    async (
      text: string,
      userId: string,
      userMeta: BasicUserProfileInfo | null,
    ) => {
      setIsCommentLoading(true);
      const tempId = Date.now().toString();

      const newComment: CommentMessage = {
        id: tempId,
        username: userMeta?.username,
        type: 'comment',
        name: userMeta?.fullName || '',
        userId,
        replyCount: 0,
        text,
        avatar: userMeta?.avatar,
        status: 'uploading',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        timestamp: Date.now(),
      };

      setComments(prev => [newComment, ...prev]);

      try {
        const result = await addCommentAPI({
          clip_id: clipId,
          text_content: text,
          user_id: userId,
          user_meta: userMeta ?? undefined,
        });

        if (result?.id) {
          updateCommentStatus(tempId, 'added', result.id);
        } else {
          updateCommentStatus(tempId, 'failed');
        }

        // Refresh comments list
        const response = await fetchComments({
          clip_id: clipId,
          pageSize: 20,
          lastEvaluatedKey: null,
        });
        setComments(response.comments);
        setLastEvaluatedKey(response.lastEvaluatedKey);
        setHasMoreComments(!!response.lastEvaluatedKey);
      } catch {
        updateCommentStatus(tempId, 'failed');
      }

      setIsCommentLoading(false);
    },
    [clipId],
  );

  const retryComment = useCallback(
    async (
      commentId: string,
      userId: string,
      userMeta: BasicUserProfileInfo | null,
    ) => {
      setIsCommentLoading(true);
      updateCommentStatus(commentId, 'uploading');

      const comment = comments.find(c => c.id === commentId);
      if (!comment) {
        setIsCommentLoading(false);
        return;
      }

      try {
        const result = await addCommentAPI({
          clip_id: clipId,
          text_content: comment.text,
          user_id: userId,
          user_meta: userMeta ?? undefined,
        });

        if (result?.id) {
          updateCommentStatus(commentId, 'added', result.id);
        } else {
          updateCommentStatus(commentId, 'failed');
        }
      } catch {
        updateCommentStatus(commentId, 'failed');
      }

      setIsCommentLoading(false);
    },
    [comments, clipId],
  );

  const deleteComment = useCallback(
    async (clipId: string, timestamp: number, userId: string) => {
      try {
        await deleteCommentAPI(clipId, timestamp, userId);
        setComments(prev =>
          prev.map(comment =>
            comment.timestamp === timestamp
              ? { ...comment, isDeleted: true }
              : comment,
          ),
        );
      } catch (error) {
        console.error(`Error deleting comment:`, error);
      }
    },
    [],
  );

  return {
    comments,
    addComment,
    retryComment,
    deleteComment,
    fetchMoreComments,
    isCommentLoading,
    isFetchingInitialComments,
    isFetchingMoreComments,
    hasMoreComments,
  };
}
